import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Subscription, switchMap, timer } from 'rxjs';
import { OrderService, MercadoPagoOrderStatus } from '../../../core/services/order.service';
import { Carts } from '../../../core/services/carts.service';

type ResultKind = 'verifying' | 'approved' | 'pending' | 'failed';

@Component({
  selector: 'app-checkout-result',
  standalone: true,
  imports: [CommonModule, ButtonModule, RouterLink],
  templateUrl: './checkout-result.component.html',
  styleUrls: ['./checkout-result.component.css']
})
export class CheckoutResultComponent implements OnInit, OnDestroy {
  readonly pollIntervalMs = 4000;
  readonly pollTimeoutMs = 120000;

  kind: ResultKind = 'verifying';
  orderId: string | null = null;
  paymentId: string | null = null;
  status: MercadoPagoOrderStatus | null = null;
  timedOut = false;
  isRetrying = false;
  errorMessage = '';

  private returnedFromFailure = false;
  private startedAt = 0;
  private pollSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private cartService: Carts,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const path = this.route.snapshot.routeConfig?.path || '';
    this.returnedFromFailure = path.includes('failure');

    this.orderId = this.route.snapshot.queryParamMap.get('order_id')
      || this.route.snapshot.queryParamMap.get('external_reference');
    this.paymentId = this.route.snapshot.queryParamMap.get('payment_id')
      || this.route.snapshot.queryParamMap.get('collection_id');

    if (!this.orderId) {
      this.kind = 'failed';
      return;
    }
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  startPolling() {
    this.stopPolling();
    this.timedOut = false;
    this.startedAt = Date.now();
    this.pollSub = timer(0, this.pollIntervalMs)
      .pipe(switchMap(() => this.orderService.getMercadoPagoOrderStatus(this.orderId!)))
      .subscribe({
        next: (status) => this.applyStatus(status),
        error: (err) => {
          this.stopPolling();
          this.errorMessage = err?.error?.message || 'No se pudo verificar el pago';
          this.kind = 'failed';
          this.cd.detectChanges();
        }
      });
  }

  private applyStatus(status: MercadoPagoOrderStatus) {
    this.status = status;
    if (status.status === 'APPROVED') {
      this.kind = 'approved';
      this.stopPolling();
      this.cartService.clearCartAfterPurchase();
    } else if (status.status === 'REJECTED' || status.status === 'EXPIRED') {
      this.kind = 'failed';
      this.stopPolling();
    } else if (this.returnedFromFailure) {
      this.kind = 'failed';
      this.stopPolling();
    } else {
      this.kind = 'pending';
      if (Date.now() - this.startedAt >= this.pollTimeoutMs) {
        this.timedOut = true;
        this.stopPolling();
      }
    }
    this.cd.detectChanges();
  }

  private stopPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  retryPayment() {
    if (!this.orderId) return;
    this.isRetrying = true;
    this.orderService.retryMercadoPagoPayment(this.orderId).subscribe({
      next: (status) => {
        if (status.initPoint) {
          window.location.href = status.initPoint;
          return;
        }
        this.isRetrying = false;
        this.status = status;
        this.cd.detectChanges();
      },
      error: (err) => {
        this.isRetrying = false;
        this.errorMessage = err?.error?.message || 'El pago ya no puede reintentarse';
        if (this.status) this.status = { ...this.status, retryable: false, initPoint: null };
        this.cd.detectChanges();
      }
    });
  }

  get canRetry(): boolean {
    return !!this.status?.retryable && !!this.status?.initPoint;
  }

  get title(): string {
    switch (this.kind) {
      case 'approved': return 'Pago completado';
      case 'failed': return 'No se completó el pago';
      case 'pending': return 'Pago en proceso';
      default: return 'Verificando tu pago...';
    }
  }

  get message(): string {
    switch (this.kind) {
      case 'approved':
        return 'Gracias por tu compra. Mercado Pago confirmó el pago y tu pedido ya está en curso.';
      case 'failed':
        return this.status?.status === 'EXPIRED'
          ? 'El tiempo para pagar este pedido expiró. Tu carrito sigue disponible para volver a intentarlo.'
          : 'El pago fue rechazado o no se completó. Puedes reintentar el pago o volver al checkout; tu carrito sigue intacto.';
      case 'pending':
        return this.timedOut
          ? 'Mercado Pago aún no confirma el pago. Te avisaremos por correo y podrás ver el estado en tus pedidos.'
          : 'Mercado Pago está procesando tu pago. Esta pantalla se actualizará automáticamente.';
      default:
        return 'Estamos confirmando tu pago con Mercado Pago, no cierres esta ventana.';
    }
  }

  get icon(): string {
    switch (this.kind) {
      case 'approved': return 'pi pi-check-circle';
      case 'failed': return 'pi pi-times-circle';
      case 'pending': return 'pi pi-clock';
      default: return 'pi pi-spin pi-spinner';
    }
  }

  get iconClass(): string {
    switch (this.kind) {
      case 'approved': return 'text-green-500';
      case 'failed': return 'text-red-500';
      default: return 'text-orange-500';
    }
  }
}
